/**
 * Spot Dashboard Component Identifiers
 * Standardized unique keys for each modular trading component on the Spot Dashboard.
 */
export const SPOT_COMPONENTS = {
  ORDER_BOOK: "ORDER_BOOK",         // Market Pair Selector + Live Order Book (Asks & Bids) [LeftTable]
  TRADE_HISTORY: "TRADE_HISTORY",   // Market Executed Trades ticker (Time, Amount, Price) [RightTable]
  CHART: "CHART",                   // TradingView / Depth Chart Toggle Module [SpotChart]
  BUY_SELL: "BUY_SELL",             // Order Placement Forms (Market, Limit, Stop-Limit, etc.) [BuyAndSell]
  BOTTOM_TABLE: "BOTTOM_TABLE",     // User Open Orders, Trade History & Stop-Limit Records [BottomTable]
  QUICK_SWAP: "QUICK_SWAP",         // Instant Convert / Quick Swap 1-Click Module [QuickSwap]
  TOP_MOVERS: "TOP_MOVERS",         // 24h Top Movers, Gainers, Losers & Volatility Tickers [TopMovers]
};

/**
 * Metadata for each component for admin display, documentation and layout builders.
 */
export const SPOT_COMPONENT_META = {
  [SPOT_COMPONENTS.ORDER_BOOK]: {
    id: SPOT_COMPONENTS.ORDER_BOOK,
    name: "Order Book & Market Pairs",
    shortName: "Order Book",
    description: "Search pairs, market filter dropdown, and live bid/ask order book",
    defaultWidth: { xs: 12, sm: 12, md: 2 },
  },
  [SPOT_COMPONENTS.TRADE_HISTORY]: {
    id: SPOT_COMPONENTS.TRADE_HISTORY,
    name: "Market Trade History",
    shortName: "Trade History",
    description: "Real-time stream of executed market trades with price, volume, and time",
    defaultWidth: { xs: 12, sm: 12, md: 2 },
  },
  [SPOT_COMPONENTS.CHART]: {
    id: SPOT_COMPONENTS.CHART,
    name: "Trading Chart (TV & Depth)",
    shortName: "Chart",
    description: "Interactive TradingView candlestick chart with Depth chart toggle",
    defaultWidth: { xs: 12, sm: 12, md: 8 },
  },
  [SPOT_COMPONENTS.BUY_SELL]: {
    id: SPOT_COMPONENTS.BUY_SELL,
    name: "Buy & Sell Order Module",
    shortName: "Buy / Sell",
    description: "Order entry forms: Market, Limit, Stop-Limit, IOC, FOK, and OCO",
    defaultWidth: { xs: 12, sm: 12, md: 8 },
  },
  [SPOT_COMPONENTS.BOTTOM_TABLE]: {
    id: SPOT_COMPONENTS.BOTTOM_TABLE,
    name: "User Orders & Trade History",
    shortName: "History Tables",
    description: "Tabbed table of user open orders, filled trades, stop-limits, and fee schedule",
    defaultWidth: { xs: 12, md: 12 },
  },
  [SPOT_COMPONENTS.QUICK_SWAP]: {
    id: SPOT_COMPONENTS.QUICK_SWAP,
    name: "Instant Convert & Swap",
    shortName: "Quick Swap",
    description: "1-Click instant currency convert and swap with live rate preview",
    defaultWidth: { xs: 12, sm: 12, md: 4 },
  },
  [SPOT_COMPONENTS.TOP_MOVERS]: {
    id: SPOT_COMPONENTS.TOP_MOVERS,
    name: "Top Movers & Gainers",
    shortName: "Top Movers",
    description: "24h gainers, losers, new high/low and market fluctuation tickers",
    defaultWidth: { xs: 12, sm: 12, md: 5 },
  },
};

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 1: DEFAULT (Paybito Classic 3-Column Standard) ────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export const DEFAULT_SPOT_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-chart-buysell",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: SPOT_COMPONENTS.CHART },
          { key: SPOT_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
      {
        colId: "col-tradehistory",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.TRADE_HISTORY }],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: SPOT_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 2: BINANCE_PRO (Order Book Left, Chart Center, Buy/Sell Right) ───
// ══════════════════════════════════════════════════════════════════════════════
export const BINANCE_PRO_SPOT_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      // Left Column: Order Book + Trade History stacked (compact width)
      {
        colId: "col-left-orderbook-trades",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0",
        components: [
          { key: SPOT_COMPONENTS.ORDER_BOOK, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.TRADE_HISTORY, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Center Column: Chart + Bottom Tables stacked
      {
        colId: "col-center-chart-orders",
        size: { xs: 12, sm: 12, md: 6 },
        className: "mb-1 px-1",
        components: [
          { key: SPOT_COMPONENTS.CHART, flex: "1.4 1 0%", height: "calc(58% - 3px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.BOTTOM_TABLE, flex: "1 1 0%", height: "calc(42% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Right Column: Buy & Sell Order Entry Module (Top) + Instant Convert (Bottom)
      {
        colId: "col-right-buysell",
        size: { xs: 12, sm: 12, md: 4 },
        className: "mb-1 p-0",
        components: [
          { key: SPOT_COMPONENTS.BUY_SELL, flex: "none", height: "calc(50% + 17px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.QUICK_SWAP, flex: "none", height: "calc(50% - 23px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 3: CHART_RIGHT_STACKED_BOTTOM (User Requested Layout) ─────────────
// ══════════════════════════════════════════════════════════════════════════════
export const CHART_RIGHT_STACKED_BOTTOM_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-beside-left",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.TRADE_HISTORY }],
      },
      {
        colId: "col-chart-buysell-right",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: SPOT_COMPONENTS.CHART },
          { key: SPOT_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "mytrades-row",
    columns: [
      {
        colId: "col-bottom-table-full-width",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: SPOT_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 4: COINBASE_ADVANCED (Order Form Left, Book Center, Chart Right) ──
// ══════════════════════════════════════════════════════════════════════════════
export const COINBASE_ADVANCED_SPOT_LAYOUT_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      // Left Column: Buy & Sell Order Entry (Top) + Quick Convert (Bottom)
      {
        colId: "col-left-buysell",
        size: { xs: 12, sm: 12, md: 4 },
        className: "mb-1 p-0",
        components: [
          { key: SPOT_COMPONENTS.BUY_SELL, flex: "none", height: "calc(50% + 17px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.QUICK_SWAP, flex: "none", height: "calc(50% - 23px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Center-Left Column: Order Book & Trades (compact width)
      {
        colId: "col-center-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 px-1",
        components: [
          { key: SPOT_COMPONENTS.ORDER_BOOK, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.TRADE_HISTORY, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
      // Right Column: Chart on top, My Trades (Bottom Table) stacked below
      {
        colId: "col-right-chart-trades",
        size: { xs: 12, sm: 12, md: 6 },
        className: "mb-1 p-0",
        components: [
          { key: SPOT_COMPONENTS.CHART, flex: "1.4 1 0%", height: "calc(58% - 3px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.BOTTOM_TABLE, flex: "1 1 0%", height: "calc(42% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 5: FOUR_COLUMN_PANORAMIC (Ultra-Wide Pro Execution Grid) ──────────
// ══════════════════════════════════════════════════════════════════════════════
export const FOUR_COLUMN_PANORAMIC_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 6, md: 2 },
        className: "mb-1 position-relative p-0 four-col-orderbook",
        components: [{ key: SPOT_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory",
        size: { xs: 12, sm: 6, md: 2 },
        className: "mb-1 p-0 four-col-trades",
        components: [{ key: SPOT_COMPONENTS.TRADE_HISTORY, props: { hideTopMovers: true } }],
      },
      {
        colId: "col-chart",
        size: { xs: 12, sm: 12, md: 5 },
        className: "mb-1 px-1 four-col-chart",
        components: [
          { key: SPOT_COMPONENTS.CHART, flex: 1, height: "calc(100% - 216px)", minHeight: 400 },
          { key: SPOT_COMPONENTS.TOP_MOVERS, flex: "none", height: "210px", minHeight: 0, maxHeight: "210px", sx: { mt: "6px" }, props: { standalone: true } },
        ],
      },
      {
        colId: "col-buysell-side",
        size: { xs: 12, sm: 12, md: 3 },
        className: "mb-1 p-0 four-col-buysell",
        components: [
          { key: SPOT_COMPONENTS.BUY_SELL, flex: 1, height: "calc(50% - 3px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.QUICK_SWAP, flex: 1, height: "calc(50% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: SPOT_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 6: CHART_LEFT_INVERTED (Chart on Left, Order Book on Right) ───────
// ══════════════════════════════════════════════════════════════════════════════
export const CHART_LEFT_INVERTED_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-chart-buysell-left",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: SPOT_COMPONENTS.CHART },
          { key: SPOT_COMPONENTS.BUY_SELL, sx: { mt: "6px" } },
        ],
      },
      {
        colId: "col-orderbook-right",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-right",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.TRADE_HISTORY }],
      },
    ],
  },
  {
    rowId: "bottom-history-row",
    columns: [
      {
        colId: "col-bottom-table",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: SPOT_COMPONENTS.BOTTOM_TABLE, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET 7: SWAPPED_BOTTOM_CENTER (Chart + History Center, Buy/Sell Bottom)─
// ══════════════════════════════════════════════════════════════════════════════
export const SWAPPED_BOTTOM_CENTER_MATRIX = [
  {
    rowId: "main-trading-row",
    className: "LetfTable",
    columns: [
      {
        colId: "col-orderbook",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 position-relative p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.ORDER_BOOK }],
      },
      {
        colId: "col-tradehistory-beside-left",
        size: { xs: 12, sm: 12, md: 2 },
        className: "mb-1 p-0 orderbooktable",
        components: [{ key: SPOT_COMPONENTS.TRADE_HISTORY }],
      },
      {
        colId: "col-chart-right",
        size: { xs: 12, sm: 12, md: 8 },
        className: "mb-1 px-1 spotchart",
        components: [
          { key: SPOT_COMPONENTS.CHART, flex: "1.4 1 0%", height: "calc(58% - 3px)", minHeight: 0 },
          { key: SPOT_COMPONENTS.BOTTOM_TABLE, flex: "1 1 0%", height: "calc(42% - 3px)", minHeight: 0, sx: { mt: "6px" } },
        ],
      },
    ],
  },
  {
    rowId: "bottom-swapped-row",
    columns: [
      {
        colId: "col-buysell-full-width",
        size: { xs: 12, md: 12 },
        className: "mb-4 p-0",
        components: [{ key: SPOT_COMPONENTS.BUY_SELL, sx: { mt: "6px" } }],
      },
    ],
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// ── PRESET REGISTRY & METADATA ────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export const SPOT_LAYOUT_PRESETS = {
  DEFAULT: DEFAULT_SPOT_LAYOUT_MATRIX,
  BINANCE_PRO: BINANCE_PRO_SPOT_LAYOUT_MATRIX,
  CHART_RIGHT_STACKED_BOTTOM: CHART_RIGHT_STACKED_BOTTOM_MATRIX,
  COINBASE_ADVANCED: COINBASE_ADVANCED_SPOT_LAYOUT_MATRIX,
  FOUR_COLUMN_PANORAMIC: FOUR_COLUMN_PANORAMIC_MATRIX,
  CHART_LEFT_INVERTED: CHART_LEFT_INVERTED_MATRIX,
  SWAPPED_BOTTOM_CENTER: SWAPPED_BOTTOM_CENTER_MATRIX,
};

export const SPOT_LAYOUT_PRESET_LIST = [
  {
    id: "DEFAULT",
    settingId: 1,
    name: "Classic",
    description: "Left OrderBook | Center Chart & Buy/Sell | Right Trade History | Bottom Table",
    matrix: DEFAULT_SPOT_LAYOUT_MATRIX,
  },
  {
    id: "BINANCE_PRO",
    settingId: 2,
    name: "Advanced",
    description: "Left OrderBook & Trades | Center Chart & History | Right Buy/Sell Panel",
    matrix: BINANCE_PRO_SPOT_LAYOUT_MATRIX,
  },
  {
    id: "CHART_RIGHT_STACKED_BOTTOM",
    settingId: 3,
    name: "Chart Right",
    description: "Left OrderBook & Trades | Right Chart & Buy/Sell | Bottom My Trades",
    matrix: CHART_RIGHT_STACKED_BOTTOM_MATRIX,
  },
  {
    id: "COINBASE_ADVANCED",
    settingId: 4,
    name: "Pro Trader",
    description: "Left Buy/Sell & Convert | Center OrderBook & Trades | Right Chart & My Trades",
    matrix: COINBASE_ADVANCED_SPOT_LAYOUT_MATRIX,
  },
  {
    id: "FOUR_COLUMN_PANORAMIC",
    settingId: 5,
    name: "4-Column",
    description: "Col 1 OrderBook | Col 2 Trades | Col 3 Big Chart | Col 4 Buy/Sell & Convert",
    matrix: FOUR_COLUMN_PANORAMIC_MATRIX,
  },
  {
    id: "CHART_LEFT_INVERTED",
    settingId: 6,
    name: "Chart Left",
    description: "Left Big Chart & Buy/Sell | Right OrderBook & Trade History | Bottom Table",
    matrix: CHART_LEFT_INVERTED_MATRIX,
  },
  {
    id: "SWAPPED_BOTTOM_CENTER",
    settingId: 7,
    name: "Swapped",
    description: "Left 2 Tables | Center Chart & My Trades | Full-Width Buy/Sell at Bottom",
    matrix: SWAPPED_BOTTOM_CENTER_MATRIX,
  },
];

/**
 * Global active matrix currently used by the dashboard.
 * Change this variable or use setSpotLayoutPreset() / the UI Switcher to test.
 */
export let ACTIVE_SPOT_LAYOUT_MATRIX = DEFAULT_SPOT_LAYOUT_MATRIX;

/**
 * Retrieve the current layout matrix (supports localStorage override for testing/admin preview)
 */
export const getSpotLayoutMatrix = () => {
  try {
    const savedPresetId = localStorage.getItem("admin_spot_layout_preset");
    if (savedPresetId && SPOT_LAYOUT_PRESETS[savedPresetId]) {
      return SPOT_LAYOUT_PRESETS[savedPresetId];
    }

    const saved = localStorage.getItem("admin_spot_layout_matrix");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (err) {
    console.error("Error reading saved spot layout matrix:", err);
  }
  return ACTIVE_SPOT_LAYOUT_MATRIX;
};

/**
 * Switch preset and broadcast to the dashboard in real-time
 */
export const setSpotLayoutPreset = (presetId) => {
  if (SPOT_LAYOUT_PRESETS[presetId]) {
    ACTIVE_SPOT_LAYOUT_MATRIX = SPOT_LAYOUT_PRESETS[presetId];
    try {
      localStorage.setItem("admin_spot_layout_preset", presetId);
      localStorage.removeItem("admin_spot_layout_matrix");
    } catch (e) {
      console.error(e);
    }
    window.dispatchEvent(
      new CustomEvent("spot_layout_change", { detail: SPOT_LAYOUT_PRESETS[presetId] })
    );
    return true;
  }
  return false;
};

/**
 * Save custom matrix
 */
export const saveSpotLayoutMatrix = (matrix) => {
  ACTIVE_SPOT_LAYOUT_MATRIX = matrix;
  try {
    localStorage.setItem("admin_spot_layout_matrix", JSON.stringify(matrix));
    localStorage.removeItem("admin_spot_layout_preset");
  } catch (err) {
    console.error("Error saving spot layout matrix:", err);
  }
  window.dispatchEvent(new CustomEvent("spot_layout_change", { detail: matrix }));
};
